import React, { FC, ReactElement } from 'react';
import { OpenCampaignCardAspectRatio, OpenCampaignCard } from '@browsing/campaign';
import { Campaign } from '@shared/types/mylounge';
import { useGridLayoutContext } from '../context/GridLayoutContext';

export enum CampaignSlotMarker {
    CUSTOM_CONTENT = 'custom-content',
}

type SlotType =
    | 'SQUARE'
    | 'LARGE_SQUARE'
    | 'HORIZONTAL_RECTANGLE'
    | 'VERTICAL_RECTANGLE'
    | 'MOBILE';

export interface SlotSize {
    aspect: OpenCampaignCardAspectRatio;
    colSpan: number;
    rowSpan: number;
}

export type Insertable = {
    slot: SlotSize;
    id: string;
    children: ReactElement;
    campaignIndex: CampaignSlotMarker.CUSTOM_CONTENT;
};

export type SlotItem = SlotSize | Insertable;

export const isInsertable = (item: SlotItem): item is Insertable =>
    'campaignIndex' in item && item.campaignIndex === CampaignSlotMarker.CUSTOM_CONTENT;

export const createInsertable = (
    slot: SlotSize,
    children: ReactElement,
    id: string,
): Insertable => ({
    slot,
    children,
    id,
    campaignIndex: CampaignSlotMarker.CUSTOM_CONTENT,
});

export const Slot: Record<SlotType, SlotSize> = {
    SQUARE: { aspect: OpenCampaignCardAspectRatio.Square, colSpan: 1, rowSpan: 1 },
    LARGE_SQUARE: { aspect: OpenCampaignCardAspectRatio.LargeSquare, colSpan: 2, rowSpan: 2 },
    HORIZONTAL_RECTANGLE: {
        aspect: OpenCampaignCardAspectRatio.HorizontalRect,
        colSpan: 2,
        rowSpan: 1,
    },
    VERTICAL_RECTANGLE: {
        aspect: OpenCampaignCardAspectRatio.VerticalRect,
        colSpan: 1,
        rowSpan: 2,
    },
    MOBILE: { aspect: OpenCampaignCardAspectRatio.Mobile, colSpan: 2, rowSpan: 1 },
};

export interface CampaignBlockProps {
    slots?: SlotItem[];
    campaignsInternal?: Campaign[];
    /**
     * Starting index for campaigns in this block.
     * Explicitly passed to avoid mutable state in context.
     * Default: 0 (will be set by DeclarativeGrid during traversal)
     */
    startIndex?: number;
}

/**
 * Renders a block of campaign cards within the grid layout.
 *
 * This component consumes campaigns from the `GridLayoutContext` and renders them
 * according to the specified slot configuration. Each slot defines the aspect ratio
 * and grid span (columns and rows) for the campaign card.
 *
 * Campaign cards are rendered with appropriate tracking based on their type:
 * - Plus exclusive campaigns: trackCreativeName='plus_exclusive'
 * - Regular campaigns: trackCreativeName='open'
 *
 * @param props - The component props
 * @param props.slots - Array of slot configurations defining the layout for each campaign card.
 *                      Each slot specifies the aspect ratio, column span, and row span.
 *                      Defaults to a single `HORIZONTAL_RECTANGLE` slot.
 *
 * @example
 * // Render a single horizontal rectangle campaign
 * <CampaignBlock />
 *
 * @example
 * // Render multiple campaigns with different layouts
 * <CampaignBlock slots={[Slot.LARGE_SQUARE, Slot.VERTICAL_RECTANGLE, Slot.SQUARE]} />
 */
export const CampaignBlock: FC<CampaignBlockProps> = ({
    slots = [Slot.HORIZONTAL_RECTANGLE],
    /**
     * Starting index for campaigns in this block.
     * Injected by DeclarativeGrid during traversal.
     * Default: 0 (when used outside DeclarativeGrid)
     */
    startIndex = 0,
}) => {
    const { campaigns, columnCount, isPlusEnabled } = useGridLayoutContext();
    let campaignIndex = startIndex;

    return (
        <>
            {slots.map(slotItem => {
                if (isInsertable(slotItem)) {
                    // Render custom content without consuming a campaign
                    const { slot, children, id } = slotItem;
                    return (
                        <div
                            key={`insertable-${id}-skip-campaign`}
                            style={{
                                gridColumn: `span ${slot.colSpan}`,
                                gridRow: `span ${slot.rowSpan}`,
                            }}
                        >
                            {children}
                        </div>
                    );
                }

                // Normal campaign rendering
                const campaign =
                    campaignIndex < campaigns.length ? campaigns[campaignIndex] : undefined;
                if (!campaign) return null;

                const { aspect, colSpan, rowSpan } = slotItem;
                const currentIndex = campaignIndex;
                campaignIndex++;

                return (
                    <OpenCampaignCard
                        key={campaign.campaignId}
                        style={{ gridColumn: `span ${colSpan}`, gridRow: `span ${rowSpan}` }}
                        aspectRatio={aspect}
                        campaign={campaign}
                        isPlusEnabled={isPlusEnabled}
                        position={`${currentIndex + 1}|${columnCount}`}
                    />
                );
            })}
        </>
    );
};
